import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { useMantineColorScheme, Text } from '@mantine/core';
import './outageLoadManagement.scss';

/*
  DailyTimeRangeSelector
  --------------------------------------------------
  An interactive 24h timeline (96 x 15‑minute slots) implemented with SVG + D3.
  Users click & drag (or touch & drag) to select a continuous time range.
  On mouse/touch release, onRangeCommit(startMinutes, endMinutes) is fired
  (endMinutes is exclusive – e.g. 60 means range covers 00:00–00:59:59).

  Props:
    initialStartMinutes (number | null)   – previously selected start (0..1439) rounded to 15.
    initialEndMinutes (number | null)     – previously selected end   (1..1440) rounded to 15.
    onRangeCommit(start, end)             – callback when user finalises selection.

  A11y:
    - The SVG has role='group' and ARIA label.
    - Keyboard support: left/right arrows move start; Shift+arrow expands end.

  Assumptions:
    - Single continuous range selection (no multi‑segment support yet).
    - Minimum granularity is fixed at 15 minutes.
*/

const TOTAL_MINUTES = 24 * 60; // 1440
const SLOT = 15;
const SLOT_COUNT = TOTAL_MINUTES / SLOT; // 96

const margin = { top: 6, right: 12, bottom: 30, left: 12 };
const trackHeight = 40;
const height = trackHeight + margin.top + margin.bottom;

function minutesToLabel(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const DailyTimeRangeSelector = ({
  initialStartMinutes = null,
  initialEndMinutes = null,
  onRangeCommit,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(800);

  // Internal selection state
  const [draftStart, setDraftStart] = useState(initialStartMinutes);
  const [draftEnd, setDraftEnd] = useState(initialEndMinutes);
  const [isDragging, setIsDragging] = useState(false);
  // dragMode: 'new' | 'extend-start' | 'extend-end'
  const [dragMode, setDragMode] = useState('new');

  // When parent resets (Delete All) it passes nulls again; sync internal draft state.
  useEffect(() => {
    setDraftStart(initialStartMinutes);
    setDraftEnd(initialEndMinutes);
  }, [initialStartMinutes, initialEndMinutes]);

  // Resize observer
  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w && Math.abs(w - width) > 2) setWidth(w);
    };
    measure();
    let ro = null;
    if ('ResizeObserver' in window) { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [width]);

  const commitRange = useCallback((s, e) => {
    if (s == null || e == null || e <= s) return;
    onRangeCommit?.(s, e);
  }, [onRangeCommit]);

  const clampToSlot = (minutes) => {
    let m = Math.max(0, Math.min(TOTAL_MINUTES, minutes));
    // Round down to nearest 15 except if it's exactly 1440
    if (m === TOTAL_MINUTES) return m; // allow 24:00 exclusive end
    return Math.floor(m / SLOT) * SLOT;
  };

  const handlePointerDown = (clientX) => {
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const innerWidth = width - margin.left - margin.right;
    const xScale = d3.scaleLinear().domain([0, TOTAL_MINUTES]).range([0, innerWidth]);
    const relX = clientX - rect.left - margin.left;
    const minutes = clampToSlot(xScale.invert(relX));
    // If there is an existing selection allow extension / shrinking
    if (draftStart != null && draftEnd != null && draftEnd > draftStart) {
      if (minutes < draftStart) {
        // Extend earlier
        setDraftStart(minutes);
        setDragMode('extend-start');
      } else if (minutes >= draftEnd) {
        // Extend later
        let newEnd = minutes + SLOT;
        if (newEnd > TOTAL_MINUTES) newEnd = TOTAL_MINUTES;
        setDraftEnd(newEnd);
        setDragMode('extend-end');
      } else {
        // Inside selection: decide nearest edge to resize
        const distToStart = minutes - draftStart;
        const distToEnd = draftEnd - minutes;
        if (distToStart < distToEnd) {
          setDragMode('extend-start');
        } else {
          setDragMode('extend-end');
        }
      }
    } else {
      // Fresh selection
      setDraftStart(minutes);
      setDraftEnd(Math.min(TOTAL_MINUTES, minutes + SLOT));
      setDragMode('new');
    }
    setIsDragging(true);
  };

  const handlePointerMove = (clientX) => {
    if (!isDragging) return;
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const innerWidth = width - margin.left - margin.right;
    const xScale = d3.scaleLinear().domain([0, TOTAL_MINUTES]).range([0, innerWidth]);
    const relX = clientX - rect.left - margin.left;
    let minutes = clampToSlot(xScale.invert(relX));
    if (minutes === TOTAL_MINUTES) minutes = TOTAL_MINUTES - SLOT; // keep inside for live drag visual
    if (draftStart == null) return;
    if (dragMode === 'new') {
      if (minutes < draftStart) {
        setDraftEnd(draftStart + SLOT);
        setDraftStart(minutes);
      } else {
        setDraftEnd(Math.min(TOTAL_MINUTES, minutes + SLOT));
      }
    } else if (dragMode === 'extend-start') {
      // Prevent crossing over end - maintain at least one slot
      const maxStart = draftEnd - SLOT;
      const newStart = Math.min(Math.max(0, minutes), maxStart);
      setDraftStart(newStart);
    } else if (dragMode === 'extend-end') {
      const minEnd = draftStart + SLOT;
      let newEnd = Math.max(minutes + SLOT, minEnd);
      if (newEnd > TOTAL_MINUTES) newEnd = TOTAL_MINUTES;
      setDraftEnd(newEnd);
    }
  };

  const handlePointerUp = () => {
    if (isDragging) {
      commitRange(draftStart, draftEnd);
    }
    setIsDragging(false);
    setDragMode('new');
  };

  // Global listeners for mouse/touch up
  useEffect(() => {
    const move = (e) => handlePointerMove(e.clientX);
    const up = () => handlePointerUp();
    if (isDragging) {
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      window.addEventListener('touchmove', touchMove, { passive: false });
      window.addEventListener('touchend', up);
    }
    function touchMove(e) { if (e.touches?.[0]) { handlePointerMove(e.touches[0].clientX); e.preventDefault(); } }
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', up);
    };
  }, [isDragging, draftStart, draftEnd]);

  // Drawing
  useEffect(() => {
    const svgEl = svgRef.current; if (!svgEl) return;
    const innerWidth = width - margin.left - margin.right;

    const svg = d3.select(svgEl)
      .attr('width', width)
      .attr('height', height)
      .style('cursor', 'crosshair')
      .attr('role', 'group')
      .attr('aria-label', '24 hour time range selector');

    svg.selectAll('*').remove();

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3.scaleLinear().domain([0, TOTAL_MINUTES]).range([0, innerWidth]);

    // Background track
    g.append('rect')
      .attr('x', 0).attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', trackHeight)
      .attr('rx', 4)
      .attr('fill', dark ? '#1f2124' : '#f5f6f8')
      .attr('stroke', dark ? '#444' : '#ccc');

    // Slot grid lines (hourly)
    const hourLines = g.append('g').attr('class', 'hour-lines');
    for (let h = 0; h <= 24; h++) {
      const xPos = x(h * 60);
      hourLines.append('line')
        .attr('x1', xPos).attr('x2', xPos)
        .attr('y1', 0).attr('y2', trackHeight)
        .attr('stroke', dark ? '#333' : '#ddd')
        .attr('stroke-width', h === 0 || h === 24 ? 1 : 0.6);
    }

    // Quarter hour subtle ticks (optional)
    const quarterLines = g.append('g').attr('class', 'quarter-lines');
    for (let i = 0; i < SLOT_COUNT; i++) {
      const m = i * SLOT;
      if (m % 60 === 0) continue; // skip hour lines already drawn
      const xPos = x(m);
      quarterLines.append('line')
        .attr('x1', xPos).attr('x2', xPos)
        .attr('y1', 0).attr('y2', trackHeight)
        .attr('stroke', dark ? '#2a2c2f' : '#eee')
        .attr('stroke-width', 0.5);
    }

    // Existing selection highlight
    if (draftStart != null && draftEnd != null) {
      g.append('rect')
        .attr('class', 'selection-range')
        .attr('x', x(draftStart))
        .attr('y', 2)
        .attr('width', Math.max(4, x(draftEnd) - x(draftStart)))
        .attr('height', trackHeight - 4)
        .attr('rx', 4)
        .attr('fill', dark ? 'rgba(140,180,255,0.35)' : 'rgba(90,140,255,0.35)')
        .attr('stroke', dark ? '#6da0ff' : '#3d7dff')
        .attr('stroke-width', 1.2);

      // Start / end handles
      const handleData = [
        { xPos: x(draftStart), key: 'start' },
        { xPos: x(draftEnd), key: 'end' },
      ];
      g.selectAll('circle.handle')
        .data(handleData)
        .enter()
        .append('circle')
        .attr('class', 'handle')
        .attr('cx', d => d.xPos)
        .attr('cy', trackHeight / 2)
        .attr('r', 6)
        .attr('fill', dark ? '#6da0ff' : '#3d7dff')
        .attr('stroke', dark ? '#102a55' : '#1b55a8')
        .attr('stroke-width', 1.2);

      // Label overlay
      const label = `${minutesToLabel(draftStart)} – ${minutesToLabel(draftEnd === TOTAL_MINUTES ? TOTAL_MINUTES - 1 : draftEnd)}`;
      g.append('text')
        .attr('x', x(draftStart) + 6)
        .attr('y', 14)
        .text(label)
        .attr('fill', dark ? '#dbe4ff' : '#1c2e57')
        .style('font-size', '12px')
        .style('font-family', 'Nunito, sans-serif');
    }

    // Axis
    const axis = d3.axisBottom(x)
      .tickValues(d3.range(0, TOTAL_MINUTES + 60, 60))
      .tickFormat(d => d / 60);

    const axisG = g.append('g')
      .attr('transform', `translate(0,${trackHeight})`)
      .call(axis);

    axisG.selectAll('path.domain').attr('stroke', dark ? '#555' : '#888');
    axisG.selectAll('line').attr('stroke', dark ? '#333' : '#ccc').attr('y2', 4);
    axisG.selectAll('text')
      .style('fill', dark ? '#eee' : '#222')
      .style('font-size', '11px')
      .style('font-family', 'Nunito, sans-serif');

    // Interaction overlay (transparent) appended LAST so it captures pointer events even after redraws.
    g.append('rect')
      .attr('class', 'interaction-overlay')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', trackHeight)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousedown', (e) => handlePointerDown(e.clientX))
      .on('touchstart', (e) => { if (e.touches?.[0]) handlePointerDown(e.touches[0].clientX); });
  }, [width, draftStart, draftEnd, dark]);

  // Keyboard support (basic)
  const onKeyDown = (e) => {
    if (draftStart == null || draftEnd == null) return;
    const key = e.key;
    const prevent = () => { e.preventDefault(); e.stopPropagation(); };
    if (key === 'ArrowLeft') {
      prevent();
      const newStart = Math.max(0, draftStart - SLOT);
      const newEnd = draftEnd - (e.shiftKey ? 0 : SLOT);
      if (!e.shiftKey) commitRange(newStart, newEnd);
      setDraftStart(newStart);
      setDraftEnd(newEnd);
    } else if (key === 'ArrowRight') {
      prevent();
      const newStart = draftStart + (e.shiftKey ? 0 : SLOT);
      const newEnd = Math.min(TOTAL_MINUTES, draftEnd + SLOT);
      if (!e.shiftKey) commitRange(newStart, newEnd);
      setDraftStart(newStart);
      setDraftEnd(newEnd);
    }
  };

  // Labels for display beneath chart
  const startLabel = draftStart != null ? minutesToLabel(draftStart) : '--:--';
  // For end label show the exclusive end - one minute for clarity (23:59 if 1440)
  const displayEndMinutes = draftEnd === TOTAL_MINUTES ? TOTAL_MINUTES - 1 : draftEnd;
  const endLabel = draftEnd != null ? minutesToLabel(displayEndMinutes) : '--:--';

  return (
    <div className="daily-time-range-selector mt-2" ref={containerRef} style={{zoom:"118%"}}>
      <Text size="sm" mb={4} fw={600}>Daily Time Range (drag to select)</Text>
      <svg
        ref={svgRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        style={{ outline: 'none', width: '100%', maxWidth: '100%' }}
      />
      <div className="dtrs-hint">
        <small style={{ opacity: 0.75 }}>
          Click & drag across the track to choose a {SLOT}-minute aligned range. Release to apply.
        </small>
      </div>
      <div className="dtrs-time-labels" style={{ display: 'flex', gap: '40px', marginTop: '8px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '140px' }}>
          <Text size="xs" fw={600} style={{ marginBottom: 2 }}>Start Time</Text>
          <Text size="sm" fw={500}>{startLabel}</Text>
        </div>
        <div style={{ minWidth: '140px' }}>
          <Text size="xs" fw={600} style={{ marginBottom: 2 }}>End Time</Text>
          <Text size="sm" fw={500}>{endLabel}</Text>
        </div>
      </div>
    </div>
  );
};

DailyTimeRangeSelector.propTypes = {
  initialStartMinutes: PropTypes.number,
  initialEndMinutes: PropTypes.number,
  onRangeCommit: PropTypes.func,
};

export default DailyTimeRangeSelector;
